import { LANE_COUNT, TILE } from './constants';
import { TUNING } from './tuning';

/**
 * A room is one ASCII grid per depth lane, all the same size. Characters:
 * `.` empty · `#` solid · `=` one-way platform (land on it from above) · `P` player start ·
 * `*` Starlight cell · `m` material shard (rare; kept through rewinds, spent in the lab) ·
 * `X` exit (touching it leaves the room) · `^` dust vent (an updraft rises from it) ·
 * `~` radiation zone (open air, at the room's `radiation` tier) · `w` weapon mod cache.
 * Enemies: `c` crawler · `d` drone ·
 * `R` the Ranger (a boss: the exits stay shut until it is beaten).
 */
export interface RoomDef {
  readonly id: string;
  readonly name: string;
  readonly lanes: readonly (readonly string[])[];
  /** Gravity as a share of the standard (1): the Moon's is lower. Absent: 1. */
  readonly gravity?: number;
  /** A solid top (the tutorial's): the player can't fly over the room. Absent: open sky. */
  readonly ceiling?: boolean;
  /** The radiation tier of the room's `~` zones (1–5); needed when it has any. */
  readonly radiation?: number;
}

export const T_EMPTY = 0;
export const T_SOLID = 1;
export const T_ONEWAY = 2;

export interface TilePos {
  readonly lane: number;
  readonly tx: number;
  readonly ty: number;
}

export type EnemyKind = 'crawler' | 'drone' | 'ranger';

const ENEMY_CHARS: Readonly<Record<string, EnemyKind>> = { c: 'crawler', d: 'drone', R: 'ranger' };

/** A dust vent's updraft: one tile wide, from its top row down to the vent's own row. */
export interface Vent {
  readonly lane: number;
  readonly tx: number;
  readonly top: number;
  readonly bottom: number;
}

export interface Spawn extends TilePos {
  readonly kind: EnemyKind;
}

export interface Room {
  readonly id: string;
  readonly name: string;
  readonly cols: number;
  readonly rows: number;
  /** Size in world pixels. */
  readonly width: number;
  readonly height: number;
  /** Per lane, row-major tile codes. */
  readonly tiles: readonly Uint8Array[];
  readonly start: TilePos;
  readonly cells: readonly TilePos[];
  readonly shards: readonly TilePos[];
  readonly spawns: readonly Spawn[];
  readonly exits: readonly TilePos[];
  readonly vents: readonly Vent[];
  readonly caches: readonly TilePos[];
  /** Per lane, 1 where the air is irradiated (row-major, like `tiles`). */
  readonly hot: readonly Uint8Array[];
  /** The tier of the hot tiles: they hurt a suit whose shielding is lower. */
  readonly radiation: number;
  /** Gravity as a share of the standard (scales falling speed too). */
  readonly gravity: number;
  readonly ceiling: boolean;
}

export function parseRoom(def: RoomDef): Room {
  if (def.lanes.length !== LANE_COUNT) {
    throw new Error(`${def.id}: needs ${LANE_COUNT} lanes, has ${def.lanes.length}`);
  }
  const rows = def.lanes[0]?.length ?? 0;
  const cols = def.lanes[0]?.[0]?.length ?? 0;
  if (rows === 0 || cols === 0) throw new Error(`${def.id}: empty lane`);

  const tiles: Uint8Array[] = [];
  const cells: TilePos[] = [];
  const shards: TilePos[] = [];
  const spawns: Spawn[] = [];
  const exits: TilePos[] = [];
  const ventAt: TilePos[] = [];
  const caches: TilePos[] = [];
  const hot: Uint8Array[] = [];
  let start: TilePos | undefined;

  def.lanes.forEach((grid, lane) => {
    if (grid.length !== rows) throw new Error(`${def.id}: lane ${lane} has ${grid.length} rows`);
    const codes = new Uint8Array(cols * rows);
    const heat = new Uint8Array(cols * rows);
    grid.forEach((line, ty) => {
      if (line.length !== cols) {
        throw new Error(`${def.id}: lane ${lane} row ${ty} is ${line.length} wide, not ${cols}`);
      }
      [...line].forEach((ch, tx) => {
        const at = { lane, tx, ty };
        switch (ch) {
          case '.':
            break;
          case '#':
            codes[ty * cols + tx] = T_SOLID;
            break;
          case '=':
            codes[ty * cols + tx] = T_ONEWAY;
            break;
          case 'P':
            if (start) throw new Error(`${def.id}: more than one start`);
            start = at;
            break;
          case '*':
            cells.push(at);
            break;
          case 'm':
            shards.push(at);
            break;
          case 'X':
            exits.push(at);
            break;
          case '^':
            ventAt.push(at);
            break;
          case '~':
            heat[ty * cols + tx] = 1;
            break;
          case 'w':
            caches.push(at);
            break;
          default: {
            const kind = ENEMY_CHARS[ch];
            if (kind) {
              spawns.push({ ...at, kind });
              break;
            }
            throw new Error(`${def.id}: unknown character '${ch}' at lane ${lane} ${tx},${ty}`);
          }
        }
      });
    });
    tiles.push(codes);
    hot.push(fillHotHoles(grid, heat, cols, rows));
  });

  if (!start) throw new Error(`${def.id}: no start (P)`);
  const radiation = def.radiation ?? 0;
  if (hot.some((h) => h.includes(1)) && !(radiation >= 1 && radiation <= 5)) {
    throw new Error(`${def.id}: radiation zones (~) need a radiation tier 1–5`);
  }
  // Each updraft rises until a solid tile (one-way planks let it through) or its full height.
  const vents = ventAt.map(({ lane, tx, ty }) => {
    let top = ty;
    while (
      top > ty - TUNING.ventRows + 1 &&
      top > 0 &&
      tiles[lane]?.[(top - 1) * cols + tx] !== T_SOLID
    ) {
      top--;
    }
    return { lane, tx, top, bottom: ty };
  });
  const gravity = def.gravity ?? 1;
  if (!(gravity > 0)) throw new Error(`${def.id}: gravity must be above 0`);
  return {
    id: def.id,
    name: def.name,
    cols,
    rows,
    width: cols * TILE,
    height: rows * TILE,
    tiles,
    start,
    cells,
    shards,
    spawns,
    exits,
    vents,
    caches,
    hot,
    radiation,
    gravity,
    ceiling: def.ceiling ?? false,
  };
}

/** Everything drawn in open air (not ground): inside a zone, the air at it is hot too. */
const ITEM_CHARS = new Set(['P', '*', 'm', 'w', 'c', 'd', 'R', '^', 'X', '=']);

/**
 * A cell, shard, cache, enemy, vent, plank, door or the start drawn inside a radiation zone takes
 * its tile's character, so that tile would be a hole in the zone. A run of such tiles is hot when the zone
 * closes it on both sides (left and right, or above and below).
 */
function fillHotHoles(grid: readonly string[], heat: Uint8Array, cols: number, rows: number) {
  const isItem = (tx: number, ty: number) => ITEM_CHARS.has(grid[ty]?.[tx] ?? '');
  const hotPast = (tx: number, ty: number, dx: number, dy: number) => {
    while (isItem(tx, ty)) {
      tx += dx;
      ty += dy;
    }
    return tx >= 0 && tx < cols && ty >= 0 && ty < rows && heat[ty * cols + tx] === 1;
  };
  const filled = heat.slice();
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      if (!isItem(tx, ty)) continue;
      const across = hotPast(tx, ty, -1, 0) && hotPast(tx, ty, 1, 0);
      const down = hotPast(tx, ty, 0, -1) && hotPast(tx, ty, 0, 1);
      if (across || down) filled[ty * cols + tx] = 1;
    }
  }
  return filled;
}

/** True when the air at a tile is irradiated. */
export function hotAt(room: Room, lane: number, tx: number, ty: number): boolean {
  if (tx < 0 || tx >= room.cols || ty < 0 || ty >= room.rows) return false;
  return room.hot[lane]?.[ty * room.cols + tx] === 1;
}

/**
 * Tile code at a tile position. The room's left and right edges are walls; above is open sky
 * unless the room has a ceiling, and below is open (falling out is handled by the simulation).
 */
export function tileAt(room: Room, lane: number, tx: number, ty: number): number {
  if (tx < 0 || tx >= room.cols) return T_SOLID;
  if (ty < 0) return room.ceiling ? T_SOLID : T_EMPTY;
  if (ty >= room.rows) return T_EMPTY;
  return room.tiles[lane]?.[ty * room.cols + tx] ?? T_SOLID;
}
